import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { KNOWLEDGE_ITEM_TYPES } from '../knowledgeItems/knowledgeItemModel';
import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitlyPlacedInAnotherChapter,
  isProfileFactLifecycleVisible,
} from './profilePlacementMetadata';
import { isKnowledgeFactProjectedToChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';
import {
  isStructuredAllergyFact,
  isStructuredMedicationFact,
} from './structuredManagerBoundaries';

export const DAILY_LIFE_DOMAIN_ID = 'DM_05';

const GROUPS = Object.freeze({
  morning: 'Morning',
  meals_drinks: 'Meals and drinks',
  activities_participation: 'Activities and participation',
  transitions: 'Transitions',
  rest_sleep: 'Rest and sleep',
  evening: 'Evening',
  personal_care: 'Personal care',
  flexible_routines: 'Flexible or changing routines',
});

const DEFAULT_GROUP_ORDER = Object.freeze(Object.keys(GROUPS));
const ELIGIBLE_TYPES = new Set([
  KNOWLEDGE_ITEM_TYPES.ROUTINE,
  KNOWLEDGE_ITEM_TYPES.SLEEP,
  KNOWLEDGE_ITEM_TYPES.FEEDING,
]);

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const valueKey = (value = '') => cleanText(value).toLowerCase().replace(/[.]+$/, '');
const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title);

const isEpisodicObservation = (value = '') => (
  /\b(?:today|yesterday|last night|this morning|this afternoon|this evening|for the first time|one time|once|one transition|during (?:an |the )?illness|at today's|during today's|on (?:monday|tuesday|wednesday|thursday|friday|saturday|sunday))\b/i
    .test(value)
);

const hasDurableContextForSecondaryType = (fact = {}) => {
  if (fact.knowledgeType === KNOWLEDGE_ITEM_TYPES.ROUTINE) return true;
  return /\b(?:usually|typically|routine|every|daily|most days|bedtime|wake(?:s| up)?|breakfast|lunch|dinner|meal|nap|sleep schedule|after|before)\b/i
    .test(getFactValue(fact));
};

const isEligibleFact = (fact = {}) => {
  if (!isProfileFactLifecycleVisible(fact)) return false;
  if (isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE)) return false;
  if (isStructuredMedicationFact(fact) || isStructuredAllergyFact(fact)) return false;
  if (isKnowledgeFactProjectedToChapter(fact, PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE, { includeFallback: false })) return true;
  return (fact.profileVisibilityOverride === 'pin' || !fact.needsQuickCheck)
    && ELIGIBLE_TYPES.has(fact.knowledgeType)
    && hasDurableContextForSecondaryType(fact)
    && !isEpisodicObservation(getFactValue(fact));
};

const classifyFact = (value = '') => {
  const text = cleanText(value).toLowerCase();
  if (/\b(?:flexible|less structured|varies|vary|changing routine|depends on the day|morning or evening)\b/.test(text)) return 'flexible_routines';
  if (/\b(?:shower|bath|dress|groom|brush(?:es|ing)? (?:hair|teeth)|toilet|hygiene|personal care)\b/.test(text)) return 'personal_care';
  if (/\b(?:sleep|bedtime|nap|rests?|resting|quiet time|wake during the night)\b/.test(text)) return 'rest_sleep';
  if (/\b(?:breakfast|lunch|dinner|meal|snack|food|drink|hydrat|eat)\b/.test(text)) return 'meals_drinks';
  if (/\b(?:transition|changing activities|change activities|countdown|first[ -]then|before leaving|visual schedule before)\b/.test(text)) return 'transitions';
  if (/\b(?:morning|wake(?:s| up)?|gets? up|start of the day)\b/.test(text)) return 'morning';
  if (/\b(?:evening|after dinner|end of the day|night routine)\b/.test(text)) return 'evening';
  if (/\b(?:activity|activities|walk|school|work|therapy|participat|play|exercise|outing)\b/.test(text)) return 'activities_participation';
  return '';
};

const buildHandbookCopy = (value = '', groupId = '') => {
  const text = cleanText(value).replace(/[.]+$/, '');
  const patterns = [
    [/^usually wakes? (.+)$/i, 'Usual wake time', (match) => `Usually wakes ${match[1]}.`],
    [/^(.+?) before changing activities$/i, 'Changing activities', (match) => `${match[1]} before changing activities.`],
    [/^(.+?) after dinner$/i, 'After dinner', (match) => `${match[1]} after dinner.`],
    [/^bedtime begins? with (.+)$/i, 'Bedtime routine', (match) => `Bedtime begins with ${match[1]}.`],
  ];
  const matched = patterns.find(([pattern]) => pattern.test(text));
  if (matched) {
    const match = text.match(matched[0]);
    return { headline: matched[1], explanation: matched[2](match) };
  }
  const contextualExplanations = {
    morning: 'This is part of their usual morning rhythm.',
    meals_drinks: 'This is part of their usual meals and drinks routine.',
    activities_participation: 'This is part of how they usually take part in the day.',
    transitions: 'This supports their usual transitions between activities.',
    rest_sleep: 'This is part of their usual rest and sleep rhythm.',
    evening: 'This is part of their usual evening rhythm.',
    personal_care: 'This is part of their usual personal care routine.',
    flexible_routines: 'This part of the day may change or stay flexible.',
  };
  return { headline: text, explanation: contextualExplanations[groupId] };
};

const getGroupOrder = (lens) => {
  if (lens === CARE_LENS.AUTISM) {
    return ['transitions', 'morning', 'activities_participation', 'meals_drinks', 'rest_sleep', 'evening', 'personal_care', 'flexible_routines'];
  }
  if (lens === CARE_LENS.DEMENTIA) {
    return ['morning', 'meals_drinks', 'activities_participation', 'rest_sleep', 'evening', 'personal_care', 'transitions', 'flexible_routines'];
  }
  if (lens === CARE_LENS.HOSPICE) {
    return ['rest_sleep', 'personal_care', 'meals_drinks', 'flexible_routines', 'morning', 'evening', 'activities_participation', 'transitions'];
  }
  return DEFAULT_GROUP_ORDER;
};

const deduplicateFacts = (facts = []) => {
  const seen = new Set();
  return facts.filter((fact) => {
    const key = valueKey(getFactValue(fact));
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildProvenance = (facts = []) => normalizeChapterProvenance(
  facts.flatMap((fact) => fact.sourceRefs || []),
  'daily-life-source'
);

// DM_05: every fact reaching this domain already came straight from
// normalizeKnowledgeFacts (isEligibleFact never admits a synthetic/legacy
// shim), so there is no synthetic gate here the way DM_02/DM_03 need one -
// each fact is always a real Knowledge doc. Episodic Timeline observations
// are excluded upstream by isEligibleFact/isEpisodicObservation, so they never
// reach this function to be made editable as routines.
// Classification-by-sourceType itself is shared; see knowledgeSourceAction.js.
const resolveFactSourceAction = (fact) => (
  resolveKnowledgeSourceAction(fact)
);

export const buildDailyLifeDomain = ({
  child = {},
  knowledgeFacts = [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const lens = getCareLensKey({
    selectedConditions: child.conditions || [],
    careContext: careContext || child.careContext || {},
  });
  const groupOrder = getGroupOrder(lens);
  const rankedFacts = deduplicateFacts(normalizeKnowledgeFacts(knowledgeFacts).filter(isEligibleFact))
    .map((fact, index) => ({
      fact,
      groupId: classifyFact(getFactValue(fact)) || (
        fact.profileChapterId === PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE
          ? 'activities_participation'
          : ''
      ),
      index,
    }))
    .filter((item) => item.groupId)
    .sort((left, right) => (
      Number(right.fact.profileVisibilityOverride === 'pin') - Number(left.fact.profileVisibilityOverride === 'pin')
      || groupOrder.indexOf(left.groupId) - groupOrder.indexOf(right.groupId)
      || left.index - right.index
    ));

  const groups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: rankedFacts.filter((item) => item.groupId === groupId).map(({ fact }) => ({
      id: `entry-${fact.id}`,
      knowledgeFactId: fact.id,
      ...buildHandbookCopy(getFactValue(fact), groupId),
      storyText: getFactValue(fact),
      responseGuidance: fact.responseGuidance || undefined,
      provenance: buildProvenance([fact]),
      sourcePresentation: buildProfileSourcePresentation(fact),
      sourceAction: resolveFactSourceAction(fact),
    })),
  })).filter((group) => group.entries.length);

  return {
    id: 'daily-life',
    title: PROFILE_CHAPTER_TITLES.DAILY_LIFE,
    caregiverQuestion: 'What does a typical day look like?',
    briefing: {
      items: rankedFacts.slice(0, 3).map(({ fact }) => ({
        id: `briefing-${fact.id}`,
        text: getFactValue(fact).replace(/[.]+$/, ''),
        explainedByEntryIds: [`entry-${fact.id}`],
      })),
      provenance: buildProvenance(rankedFacts.map((item) => item.fact)),
    },
    handbook: { groups },
    emptyState: {
      title: 'What does a typical day look like?',
      description: 'Add the routines, timing, activities, meals, rest, or personal-care patterns that help caregivers understand the day.',
    },
    metadata: {
      domainId: DAILY_LIFE_DOMAIN_ID,
      careFocus: lens,
      ageGroup: ageGroup || careContext?.ageGroup || 'unknown',
      contractVersion: '1',
    },
  };
};
